import React, { useEffect, useRef, useState } from 'react';
import { Icon } from 'assets/icons';
import { Button } from 'app/shared/button';
import { SimpleInput } from 'app/shared/input';
import Wysiwyg from 'app/shared/input/wysiwyg';
import { BasicInfoSection } from 'app/shared/info-section/basic';
import { GridColumn, GridRow } from 'app/layouts/grid';
import ConfirmationDialog from 'app/shared/dialogs/confirmation';
import useValidator from 'hooks/validation';
import { useFileService } from 'hooks/api/files/file';
import { useMessageService } from 'hooks/api/agents/message';
import styles from './config.module.css';

const stripHtml = (html = '') =>
  html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const VALIDATION_CONFIG = {
  title: { required: true },
  code: { required: true },
  body: {
    required: true,
    customValidator: (value) => ({
      isValid: !!stripHtml(value),
      message: 'Body is required.'
    })
  }
};

export const MessageConfig = () => {
  const { uploadDocument } = useFileService();
  const { fetchMessages, createMessage, updateMessage, deleteMessage } = useMessageService();
  const { errors, validateField, form_is_valid } = useValidator(VALIDATION_CONFIG);
  const file_input_ref = useRef(null);

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing_id, setEditingId] = useState(null);
  const [title, setTitle] = useState('');
  const [code, setCode] = useState('');
  const [body, setBody] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message_to_delete, setMessageToDelete] = useState(null);
  const [show_delete_dialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    loadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMessages = async () => {
    setLoading(true);
    const { messages: fetched } = await fetchMessages();
    setMessages(fetched);
    setLoading(false);
  };

  const resetForm = () => {
    setEditingId(null);
    setTitle('');
    setCode('');
    setBody('');
    setAttachment(null);
  };

  const handleTitleChange = (value) => {
    setTitle(value);
    validateField('title', value.trim());
  };

  const handleCodeChange = (value) => {
    setCode(value);
    validateField('code', value.trim());
  };

  const handleBodyChange = (value) => {
    setBody(value);
    validateField('body', value);
  };

  const handleEdit = (message) => {
    setEditingId(message.id);
    setTitle(message.title || '');
    setCode(message.code || '');
    setBody(message.body || '');
    setAttachment(message.attachment || null);
    validateField('title', (message.title || '').trim());
    validateField('code', (message.code || '').trim());
    validateField('body', message.body || '');
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    e.target.value = null;
    if (!file) return;

    setUploading(true);
    const data = new FormData();
    data.append('file', file);
    const payload = await uploadDocument({ data });
    setUploading(false);

    if (!payload) return;
    setAttachment({
      filename: file.name,
      url: payload.location,
      content_type: file.type || 'application/pdf'
    });
  };

  const handleRemoveAttachment = () => setAttachment(null);

  const handleSave = async () => {
    const trimmed_title = title.trim();
    const trimmed_code = code.trim();
    const title_is_valid = validateField('title', trimmed_title);
    const code_is_valid = validateField('code', trimmed_code);
    const body_is_valid = validateField('body', body);
    if (!title_is_valid || !code_is_valid || !body_is_valid) return;

    setSaving(true);
    const payload = { title: trimmed_title, code: trimmed_code, body, attachment };

    if (editing_id) {
      const updated = await updateMessage(editing_id, { data: payload });
      setSaving(false);
      if (!updated) return;

      setMessages((prev) =>
        prev.map((message) => (message.id === editing_id ? { ...message, ...payload } : message))
      );
      resetForm();
      return;
    }

    const { message } = await createMessage(payload);
    setSaving(false);
    if (!message?.id) return;

    setMessages((prev) => [message, ...prev]);
    resetForm();
  };

  const handleDeleteClick = (message) => {
    setMessageToDelete(message);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async (permitted) => {
    if (permitted && message_to_delete) {
      const deleted = await deleteMessage(message_to_delete.id);
      if (deleted) {
        setMessages((prev) => prev.filter(({ id }) => id !== message_to_delete.id));
        if (editing_id === message_to_delete.id) resetForm();
      }
    }
    setMessageToDelete(null);
    setShowDeleteDialog(false);
  };

  return (
    <div className={styles.stack}>
      <BasicInfoSection title="Messages">
        {!loading && messages.length === 0 && (
          <div className={styles.placeholder}>No messages created yet.</div>
        )}
        {messages.length > 0 && (
          <div className={styles.resourceList}>
            {messages.map((message) => (
              <div key={message.id} className={styles.resourceItem}>
                <div className={styles.resourceHeader}>
                  <div className={styles.resourceTitle}>{message.title}</div>
                  <div className={styles.resourceActions}>
                    <Icon name="edit" width={16} height={16} onClick={() => handleEdit(message)} />
                    <span className={styles.deleteIcon}>
                      <Icon
                        name="trash"
                        width={16}
                        height={16}
                        onClick={() => handleDeleteClick(message)}
                      />
                    </span>
                  </div>
                </div>
                {message.attachment?.filename && (
                  <div className={styles.resourceAttachment}>📎 {message.attachment.filename}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </BasicInfoSection>

      <BasicInfoSection title={editing_id ? 'Edit Message' : 'Create Message'}>
        <GridRow num_of_columns={6}>
          <GridColumn span={4}>
            <SimpleInput
              input_id="message_title"
              placeholder="Message title"
              value={title}
              onInput={handleTitleChange}
              error={errors.title}
              required
            />
          </GridColumn>
          <GridColumn span={2}>
            <SimpleInput
              input_id="message_code"
              placeholder="e.g. welcome_email"
              value={code}
              onInput={handleCodeChange}
              error={errors.code}
              required
            />
          </GridColumn>
        </GridRow>

        <div className={styles.formRow}>
          <input
            ref={file_input_ref}
            type="file"
            accept="application/pdf"
            className={styles.hiddenFileInput}
            onChange={handleFileSelect}
          />
          <Wysiwyg
            value={body}
            onInput={handleBodyChange}
            placeholder="Write a message..."
            error={errors.body}
            extra_tools={[
              {
                key: 'attach_pdf',
                title: attachment ? 'Replace PDF' : 'Upload PDF',
                disabled: uploading,
                icon: <Icon name="attach_pdf" height={15} width={15} />,
                onClick: () => file_input_ref.current.click()
              }
            ]}
          />
          {attachment && (
            <span className={styles.attachmentName}>
              📎 {attachment.filename}
              <Icon name="close" height={12} width={12} onClick={handleRemoveAttachment} />
            </span>
          )}
        </div>

        <div className={styles.formActions}>
          {editing_id && <Button text="Cancel" type="secondary" onClick={resetForm} />}
          <Button text="Save" onClick={handleSave} loading={saving} disabled={!form_is_valid} />
        </div>
      </BasicInfoSection>

      <ConfirmationDialog
        title="Delete Message"
        message="Are you sure you want to delete this message?"
        callback={handleConfirmDelete}
        is_open={show_delete_dialog}
      />
    </div>
  );
};
